import React, { useState, useEffect, useRef } from 'react';

const B = '#2563eb';

const STEPS_DIRECTEUR = [
  {
    target: 'tab-dashboard',
    title: '📊 Votre tableau de bord',
    desc: "Ici vous avez une vue d'ensemble : interventions du jour, chiffre d'affaires, prochains rendez-vous. C'est votre point de départ chaque matin.",
    position: 'bottom',
  },
  {
    target: 'tab-planning',
    title: '📅 Votre planning',
    desc: "Cliquez sur n'importe quelle date pour créer une intervention. Ajoutez vos photos, notes et faites signer le client directement depuis l'app.",
    position: 'bottom',
  },
  {
    target: 'tab-clients',
    title: '👤 Vos clients',
    desc: "Retrouvez tous vos clients, leur historique d'interventions et envoyez-leur des rappels ou relances en un clic.",
    position: 'bottom',
  },
  {
    target: 'tab-factures',
    title: '📄 Factures & PDF',
    desc: "Générez des bons d'intervention et factures PDF professionnels automatiquement. Logo, SIRET, TVA — tout est pré-rempli depuis votre profil.",
    position: 'bottom',
  },
  {
    target: 'tab-compte',
    title: '⚙️ Mon compte',
    desc: "Modifiez votre profil, logo, informations de facturation et gérez votre abonnement. Vous pouvez aussi inviter votre équipe depuis l'onglet Équipe.",
    position: 'bottom',
  },
];

const STEPS_AGENT = [
  {
    target: 'tab-dashboard',
    title: '📊 Le tableau de bord',
    desc: "Vue d'ensemble : interventions du jour et prochains rendez-vous de l'équipe. Votre point de départ chaque matin.",
    position: 'bottom',
  },
  {
    target: 'tab-planning',
    title: '📅 Le planning',
    desc: "Consultez le planning de l'équipe. Touchez une intervention pour voir le détail, ajouter vos photos et notes, et faire signer le client sur place.",
    position: 'bottom',
  },
  {
    target: 'tab-compte',
    title: '⚙️ Mon compte',
    desc: "Modifiez vos informations personnelles et vos préférences depuis cet onglet.",
    position: 'bottom',
  },
];

export default function Tutorial({ onFinish, setActiveTab, isAgent }) {
  const STEPS = isAgent ? STEPS_AGENT : STEPS_DIRECTEUR;
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);

  const current = STEPS[step];

  useEffect(() => {
    const el = document.getElementById(current.target);
    if (!el) { setRect(null); return; }

    // Naviguer vers le bon tab pour que l'élément soit visible
    if (setActiveTab) {
      const tabName = current.target.replace('tab-', '');
      setActiveTab(tabName);
    }

    // Petit délai pour laisser le DOM se mettre à jour
    const t = setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect(r);
    }, 100);
    return () => clearTimeout(t);
  }, [step, current.target, setActiveTab]);

  const next = () => {
    if (step < STEPS.length - 1) setStep(s => s + 1);
    else finish();
  };

  const prev = () => { if (step > 0) setStep(s => s - 1); };

  const finish = () => {
    localStorage.setItem('planifpro_tutorial_done', '1');
    onFinish();
  };

  // Position du tooltip par rapport à l'élément ciblé
  const getTooltipStyle = () => {
    if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

    const TW = Math.min(340, window.innerWidth - 32);
    let top = rect.bottom + 16;
    let left = rect.left + rect.width / 2 - TW / 2;

    // Rester dans la fenêtre
    left = Math.max(16, Math.min(left, window.innerWidth - TW - 16));
    if (top + 260 > window.innerHeight) top = rect.top - 260 - 8;

    return { position: 'fixed', top, left, width: TW };
  };

  return (
    <>
      {/* Overlay sombre */}
      <div
        onClick={finish}
        style={{
          position: 'fixed', inset: 0, zIndex: 9000,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(2px)',
        }}
      />

      {/* Spotlight autour de l'élément ciblé */}
      {rect && (
        <div style={{
          position: 'fixed',
          top: rect.top - 6,
          left: rect.left - 6,
          width: rect.width + 12,
          height: rect.height + 12,
          borderRadius: '14px',
          boxShadow: `0 0 0 4px ${B}, 0 0 0 9999px rgba(0,0,0,0.55)`,
          zIndex: 9001,
          pointerEvents: 'none',
          transition: 'all 0.3s cubic-bezier(.4,0,.2,1)',
        }} />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          ...getTooltipStyle(),
          zIndex: 9002,
          background: '#fff',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          border: `1.5px solid ${B}30`,
          transition: 'all 0.25s cubic-bezier(.4,0,.2,1)',
        }}
      >
        {/* Indicateur étapes */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
          {STEPS.map((_, i) => (
            <div
              key={i}
              style={{
                height: '4px',
                flex: 1,
                borderRadius: '2px',
                background: i <= step ? B : '#e5e7eb',
                transition: 'background 0.3s',
              }}
            />
          ))}
        </div>

        <p style={{ fontSize: '11px', fontWeight: 700, color: B, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
          Étape {step + 1} sur {STEPS.length}
        </p>
        <h3 style={{ fontSize: '17px', fontWeight: 800, color: '#111827', marginBottom: '10px', letterSpacing: '-0.01em' }}>
          {current.title}
        </h3>
        <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: 1.6, marginBottom: '20px' }}>
          {current.desc}
        </p>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {step > 0 && (
            <button
              onClick={prev}
              style={{ padding: '10px 16px', background: 'transparent', color: '#6b7280', border: '1.5px solid #e5e7eb', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}
            >← Retour</button>
          )}
          <button
            onClick={next}
            style={{ flex: 1, padding: '11px', background: B, color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '14px', boxShadow: `0 4px 14px ${B}40` }}
          >
            {step < STEPS.length - 1 ? 'Suivant →' : '🎉 Commencer !'}
          </button>
          <button
            onClick={finish}
            style={{ padding: '10px 14px', background: 'transparent', color: '#9ca3af', border: 'none', cursor: 'pointer', fontSize: '13px' }}
          >Passer</button>
        </div>
      </div>
    </>
  );
}
